// Spec §17 / Phase B6 — notification template Handlebars live preview.
// Lets the coordinator paste a sample variable JSON and see the rendered
// body/subject without sending an actual message. Calls the BE
// /notification-template/render-test endpoint so the same Handlebars
// engine that ships SMS/email is what's previewed.

import { useEffect, useState } from "react"
import {
  Alert,
  Badge,
  Button,
  Code,
  Divider,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core"
import { useTranslation } from "react-i18next"
import { NotificationTemplateApi } from "../../apis"
import type { INotificationTemplate, NotificationChannel } from "../../apis/notification-template"
import HttpHandler from "../../utils/http-handler"

type Props = {
  opened: boolean
  onClose: () => void
  template: INotificationTemplate | null
}

const SAMPLE_VARS_BY_KEY: Record<string, Record<string, unknown>> = {
  ASSIGN_DELIVERY: {
    deliveryNote: { refCode: "DN-2026-0001", deliveryDate: "2026-04-29" },
    driver      : { firstName: "Бат", lastName: "Б" }
  },
  POD_VERIFIED: {
    deliveryNote: { refCode: "DN-2026-0001" },
    receiver    : { name: "Дорж", phone: "99119911" }
  }
}

function defaultVarsForKey (key?: string): string {
  const sample = key ? SAMPLE_VARS_BY_KEY[key] : null
  return JSON.stringify(sample ?? { name: "Дорж", refCode: "DN-2026-0001" }, null, 2)
}

export function NotificationPreviewModal ({ opened, onClose, template }: Props) {
  const { t } = useTranslation("settings")
  const [varsJson, setVarsJson] = useState("")
  const [bodyOverride, setBodyOverride] = useState<string | null>(null)
  const [rendered, setRendered] = useState<{ subject?: string | null; body: string } | null>(null)
  const [errors, setErrors] = useState<string[]>([])
  const [parseError, setParseError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [networkError, setNetworkError] = useState<string | null>(null)

  useEffect(() => {
    if (!opened) return
    setVarsJson(defaultVarsForKey(template?.templateKey))
    setBodyOverride(null)
    setRendered(null)
    setErrors([])
    setParseError(null)
    setNetworkError(null)
  }, [opened, template?.templateKey])

  const onPreview = async () => {
    if (!template) return
    setParseError(null)
    setNetworkError(null)
    setRendered(null)
    setErrors([])

    let variables: Record<string, unknown>
    try {
      variables = varsJson.trim() ? JSON.parse(varsJson) : {}
      if (typeof variables !== "object" || variables === null || Array.isArray(variables)) {
        throw new Error("variables must be a JSON object")
      }
    } catch (err) {
      setParseError((err as Error).message)
      return
    }

    setSubmitting(true)
    try {
      const result = await NotificationTemplateApi.renderTest({
        body     : bodyOverride ?? template.body,
        subject  : template.subject ?? null,
        variables
      })
      setRendered({ subject: result.subject ?? template.subject, body: result.body })
      setErrors(result.errors ?? [])
    } catch (err) {
      setNetworkError((err as HttpHandler)?.message ?? "Render failed")
    } finally {
      setSubmitting(false)
    }
  }

  if (!template) return null

  const channelLabel = (ch: string) =>
    t(`notification.options.channel.${ch as NotificationChannel}`)

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <Title order={5}>{t("notification.preview.title")}</Title>
          <Badge size="xs" variant="light" color="cyan">
            {channelLabel(template.channel)}
          </Badge>
        </Group>
      }
      size="xl"
    >
      <Stack gap="md">
        <Stack gap={4}>
          <Text fz={14} fw={600} tt="uppercase" c="dimmed">
            {t("notification.preview.templateLabel")}
          </Text>
          <Textarea
            value={bodyOverride ?? template.body}
            onChange={(e) => setBodyOverride(e.currentTarget.value)}
            minRows={4}
            autosize
            styles={{ input: { fontFamily: "var(--mantine-font-family-monospace)" } }}
          />
        </Stack>

        <Stack gap={4}>
          <Text fz={14} fw={600} tt="uppercase" c="dimmed">
            {t("notification.preview.variablesLabel")}
          </Text>
          <Textarea
            value={varsJson}
            onChange={(e) => setVarsJson(e.currentTarget.value)}
            minRows={6}
            autosize
            styles={{ input: { fontFamily: "var(--mantine-font-family-monospace)" } }}
            error={parseError ?? undefined}
          />
        </Stack>

        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t("common.close")}
          </Button>
          <Button onClick={onPreview} loading={submitting}>
            {t("notification.preview.previewButton")}
          </Button>
        </Group>

        {networkError && <Alert color="red" variant="light">{networkError}</Alert>}

        {errors.length > 0 && (
          <Alert color="yellow" variant="light">
            <Stack gap={2}>
              {errors.map((e, i) => <Text key={i} size="sm">{e}</Text>)}
            </Stack>
          </Alert>
        )}

        {rendered && (
          <>
            <Divider />
            <Stack gap={4}>
              <Text fz={14} fw={600} tt="uppercase" c="dimmed">
                {t("notification.preview.renderedOutput")}
              </Text>
              {rendered.subject && (
                <>
                  <Text size="sm" c="dimmed">{t("notification.preview.renderedSubject")}:</Text>
                  <Code block>{rendered.subject}</Code>
                </>
              )}
              <Text size="sm" c="dimmed">{t("notification.preview.renderedBody")}:</Text>
              <Code block>{rendered.body}</Code>
            </Stack>
          </>
        )}
      </Stack>
    </Modal>
  )
}
